import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  add,
  buildLedger,
  consolidate,
  estimateCt,
  incomeStatement,
  money,
  subtract,
  sumByTypes,
  sumDebits,
  trialBalance,
  zero,
} from "@core/index";
import { useAppStore } from "@state/store";
import { useLedger } from "@ui/useLedger";
import { MoneyText, TieOutBadge, LoadingSkeleton, EmptyState, ErrorState } from "@ui/components";

const CASH_CODES = ["1100", "1105"];

export function Dashboard() {
  const { status, error, entities, accounts, entries, settings, entityId, scopeName, base, isEmpty } =
    useLedger();
  const load = useAppStore((s) => s.load);

  const data = useMemo(() => {
    const tb = trialBalance(entries, accounts, base, scopeName, entityId);
    const ledger = buildLedger(entries, accounts, base, entityId);
    const cash = CASH_CODES.reduce(
      (acc, code) => add(acc, ledger.get(code)?.balance ?? zero(base)),
      zero(base),
    );
    const vat = (() => {
      const input = ledger.get("1450")?.balance ?? zero(base);
      const output = ledger.get("2100")?.balance ?? zero(base);
      return { input, output, payable: subtract(output, input) };
    })();
    const pnl = incomeStatement(entries, accounts, base, entityId);
    const ct = estimateCt({
      profitBeforeTax: pnl.profitBeforeTax,
      revenue: pnl.revenue,
      rate: settings.ctRate,
      threshold: settings.ctThreshold,
      sbReliefRevenueCeiling: settings.sbReliefRevenueCeiling,
    });
    const group = consolidate(entries, accounts, entities, base);
    return { tb, cash, vat, ct, group };
  }, [entries, accounts, base, entityId, scopeName, entities, settings]);

  const recent = useMemo(
    () =>
      [...entries]
        .filter((e) => entityId === undefined || e.entityId === entityId)
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
        .slice(0, 6),
    [entries, entityId],
  );

  const entityName = (id: string) => entities.find((e) => e.id === id)?.name ?? id;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="sub">{scopeName} · FY 2026 · all figures in {base}</div>
        </div>
        <div className="actions">
          <Link className="btn primary" to="/journal">
            ＋ New entry
          </Link>
        </div>
      </div>

      {status === "loading" ? (
        <LoadingSkeleton rows={4} />
      ) : status === "error" ? (
        <ErrorState
          title="Couldn’t load ledger data"
          message={error ?? "The local ledger store didn’t respond. Your data is safe — this is a read error, nothing was written."}
          onRetry={load}
        />
      ) : isEmpty ? (
        <EmptyState
          title="No transactions yet"
          message="Post your first journal entry, or import a trial balance from your existing tool to see live tie-out and statements."
        >
          <Link className="btn primary" to="/journal">
            ＋ New entry
          </Link>
          <Link className="btn secondary" to="/import-export">
            Import CSV
          </Link>
        </EmptyState>
      ) : (
        <>
          <div className="grid kpi">
            <div className="card kpi-tile">
              <div className="card-body">
                <div className="label">Tie-out status</div>
                <div className="value" style={{ fontSize: "var(--fs-lg)", marginTop: "var(--s3)" }}>
                  <TieOutBadge tiesOut={data.tb.tiesOut} delta={data.tb.delta} label="Balanced" />
                </div>
                <div className="delta">
                  {data.tb.tiesOut
                    ? `Debits = credits across ${entities.length} entities`
                    : "Trial balance does not tie — review entries"}
                </div>
              </div>
            </div>
            <div className="card kpi-tile">
              <div className="card-body">
                <div className="label">Cash &amp; bank</div>
                <div className="value">
                  <MoneyText value={data.cash} />
                </div>
                <div className="delta">AED current + USD accounts</div>
              </div>
            </div>
            <div className="card kpi-tile">
              <div className="card-body">
                <div className="label">VAT payable (net)</div>
                <div className="value">
                  <MoneyText value={data.vat.payable} />
                </div>
                <div className="delta">
                  Output <MoneyText value={data.vat.output} /> − Input <MoneyText value={data.vat.input} />
                </div>
              </div>
            </div>
            <div className="card kpi-tile">
              <div className="card-body">
                <div className="label">CT provision (est.)</div>
                <div className="value">
                  <MoneyText value={data.ct.provision} />
                </div>
                <div className="delta">
                  {data.ct.reliefApplied
                    ? "Small Business Relief applied"
                    : `9% on taxable income above ${money(settings.ctThreshold, base).amount / 100 / 1000}k`}
                </div>
              </div>
            </div>
          </div>

          <div className="grid entities mt4">
            {entities.map((en) => {
              const etb = trialBalance(entries, accounts, base, en.name, en.id);
              const eledger = buildLedger(entries, accounts, base, en.id);
              const netAssets = subtract(
                sumByTypes(eledger, accounts, ["asset"], base),
                sumByTypes(eledger, accounts, ["liability"], base),
              );
              return (
                <div className="card" key={en.id}>
                  <div className="card-body">
                    <div className="row between">
                      <h3>{en.name}</h3>
                      <span className="badge neutral">
                        {en.kind === "freezone" ? "Free Zone" : "Mainland"}
                      </span>
                    </div>
                    <div className="row between mt3">
                      <span className="muted">Net assets</span>
                      <MoneyText value={netAssets} />
                    </div>
                    <div className="row between mt3">
                      <span className="muted">Base currency</span>
                      <span>{en.baseCurrency}</span>
                    </div>
                    <div className="row between mt3">
                      <span className="muted">Tie-out</span>
                      <TieOutBadge tiesOut={etb.tiesOut} delta={etb.delta} label="0.00" />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="card">
              <div className="card-body">
                <div className="row between">
                  <h3>Intercompany</h3>
                  {data.group.unmatched.length === 0 ? (
                    <span className="badge good">
                      <span className="dot" /> Matched
                    </span>
                  ) : (
                    <span className="badge warn">
                      <span className="dot" /> {data.group.unmatched.length} to review
                    </span>
                  )}
                </div>
                <div className="row between mt3">
                  <span className="muted">Eliminations</span>
                  <MoneyText value={data.group.eliminations} />
                </div>
                <div className="row between mt3">
                  <span className="muted">Unmatched</span>
                  <span className={`num${data.group.unmatched.length ? " neg" : ""}`}>
                    {data.group.unmatched.length}
                  </span>
                </div>
                <div className="mt3">
                  <Link className="btn secondary sm" to="/trial-balance">
                    Review eliminations
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt4">
            <div className="card-head">
              <h2>Recent journal entries</h2>
              <span className="top-spacer" />
              <Link className="btn secondary sm" to="/journal">
                View all
              </Link>
            </div>
            <div className="card-body tight">
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Ref</th>
                      <th>Description</th>
                      <th>Entity</th>
                      <th className="num">Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((e) => (
                      <tr key={e.id}>
                        <td>{e.date}</td>
                        <td className="acctcode">{e.ref}</td>
                        <td>{e.description}</td>
                        <td>{entityName(e.entityId)}</td>
                        <td className="num">
                          <MoneyText value={sumDebits(e.lines, base)} />
                        </td>
                        <td>
                          <span className={`badge ${e.status === "posted" ? "good" : "neutral"}`}>
                            {e.status === "posted" ? "Posted" : "Draft"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
